import React from "react";
import clsx from 'clsx';
import { withStyles } from '@material-ui/styles';
import {
  Menu,
  MenuItem
} from '@material-ui/core';
import Color from "color";

import withLocationSearchFilter from '../../../../hocs/with_location_search_filter.js';
import withCurrentUser from '../../../../hocs/with_current_user.js';
import withShowToast from '../../../../hocs/with_show_toast.js';
import withShowModal from '../../../../hocs/with_show_modal.js';
import withScrollHandler from "../../../../hocs/with_scroll_handler.js";
import { compose } from 'redux';

import pageStyles from "../../../styles/page_styles.js";
import { infoPageStyles } from "../../base_info_page.js";

import I18n from "../../../../base/i18n.js";

import BaseInfoPage from "../../base_info_page.js";
import { ButtonVariantOutlined } from "../../../../base/buttons/constants.js";
import Button from "../../../../base/buttons/button.js";
import ButtonLink from "../../../../base/links/button_link.js";

import { AdminProjectModel } from "../../../../models/projects/admin/project_model.js";
import ProjectStatusEnum from "../../../../enums/project_status.js";

import NewProjectUseCase from "../../../../use_cases/admin/projects/projects/new_project_use_case.js";
import ShowProjectUseCase from "../../../../use_cases/admin/projects/projects/show_project_use_case.js";
import SaveProjectUseCase from "../../../../use_cases/admin/projects/projects/save_project_use_case.js";
import DeleteProjectUseCase from "../../../../use_cases/admin/projects/projects/delete_project_use_case.js";

import SetProjectStatusUseCase from "../../../../use_cases/admin/projects/projects/set_project_status_use_case.js";

import PublishProjectUseCase from "../../../../use_cases/admin/projects/projects/publish_project_use_case.js";
import UnpublishProjectUseCase from "../../../../use_cases/admin/projects/projects/unpublish_project_use_case.js";

import InfoPageHeader from "../../components/info_page_header.js";
import NavigationTabs from "../../components/navigation_tabs.js";

import ProjectInfoTab from "./components/project_info_tab.js";
import ProjectInvestmentsTab from "./components/project_investments_tab.js";

import WebRoutes from "../../../../web/routes.js";
import AdminRoutes from "../../../routes.js";

const styles = theme => ({
  preview: {
    marginLeft: "20px",
    padding: "4px 15px"
  },
  publish: {
    "& + $status": {
      marginLeft: "20px",
    }
  },
  published: {
    backgroundColor: theme.palette.greenMiddle,
    "&:hover": {
      backgroundColor: Color(theme.palette.greenMiddle).darken(0.2).string(),
    },
  },
  notPublished: {
    backgroundColor: theme.palette.greenMiddle,
    "&:hover": {
      backgroundColor: Color(theme.palette.greenMiddle).darken(0.2).string(),
    },
  },
  status: {
    
  },
});

const ProjectStatusesMenu = "project_statuses_menu";

const InfoTab        = "info";
const InvestmentsTab = "investments";

class ProjectInfoPage extends BaseInfoPage {

  constructor(props) {
    super(props);

    this.tabs = [
      {
        identifier: InfoTab,
        label: I18n.t("admin.projects.project.tabs.info")
      },
      {
        identifier: InvestmentsTab,
        label: I18n.t("admin.projects.project.tabs.investments"),
        queryParams: {
          statuses: ["applied", "paid"]
        }
      }
    ]

    if(!this.state.currentTab) {
      this.state.currentTab = this.tabs[0].identifier;
    }
    this.state.filesIsLoaded = true;
  }

  componentDidMount() {
    super.componentDidMount();
    this.updateInvestmentParams();
  }

  componentDidUpdate(prevProps, prevState) {
    super.componentDidUpdate(prevProps, prevState);
    this.updateInvestmentParams();
  }

  defaultItem() {
    return {
      slug: this.identifier,
      key: "notLoadedItem"
    }
  }

  deleteConfirmMessage() {
    return I18n.t("admin.projects.project.delete.confirm_message", {
      name: this.dataModel.displayValue(this.item)
    });
  }

  pageSubtitle() {
    const { loading } = this.state;
    if(!this.identifier || loading || !this.item.isPublished) {
      return "";
    }
    return `${AdminProjectModel.fieldDisplayName("status")}: ${ProjectStatusEnum.label(this.item.status)}`;
  }

  onItemChange(changedItem) {
    this.item = {
      ...this.item,
      ...changedItem
    };
    this.updateIsLoadingFiles();
  }

  updateIsLoadingFiles = () => {
    var filesIsLoaded = true;

    this.item.projectImages.forEach(image => filesIsLoaded = filesIsLoaded && !image.loading);
    this.item.documents.forEach(file => filesIsLoaded = filesIsLoaded && !file.loading);

    this.setState({
      filesIsLoaded: filesIsLoaded
    })

  }

  beforeSaveProcessing() {
    this.item.projectImages = (this.item.projectImages || []).filter((image) => {
      const { galleryImageId, galleryImage } = image;
      return galleryImageId || galleryImage;
    })

    this.item.documents = (this.item.documents || []).filter((document) => {
      const { pdfId, pdf } = document;
      return pdfId || pdf;
    })
  }

  afterSaveSuccess() {
    if(this.publishClicked) {
      this.forceUpdate();
      if(!this.item.isPublished) {
        this.publish();
      }
      this.publishClicked = false;
      return;
    }
    this.props.showSuccessMessage(this.savedMessage(!this.identifier));
    this.publishClicked = false;
  }

  afterSaveFail() {
    this.publishClicked = false;
  }

  onOpenStatusDropdown = (event) => {
    this.setState({
      anchor: event.target
    })
  }

  onCloseStatusDropdown = (status) => {
    this.updateStatus(status);
    this.setState({
      anchor: undefined
    })
  }

  updateStatus(status) {
    if(!status) {
      return;
    }
    this.setState({
      submit: true,
      errorData: undefined
    });

    SetProjectStatusUseCase.execute(this.identifier, status)
    .then((result) => {
      this.props.showSuccessMessage(I18n.t("admin.projects.project.toast.status_updated"));

      const { data: { project } } = result;
      
      this.item = {
        ...this.item,
        ...project,
        key: project.updatedAt
      }
      this.setState({
        submit: false,
      });
      this.forceUpdate();
    })
    .catch((result) => {
      const { errorData } = result;

      this.setState({
        submit: false,
        errorData: errorData
      });
      this.props.showServerErrorIfNeed(result);
    })
  }

  onPublishClicked = () => {
    this.publishClicked = true;
    if(this.item.isPublished) {
      this.confirmUnpublishing();
    }
    else {
      this.confirmPublishing();
    }
  }

  confirmPublishing = () => {
    this.props.showConfirmModal({
      title: I18n.t("admin.projects.project.publishing.title"),
      message: I18n.t("admin.projects.project.publishing.message", {
        name: AdminProjectModel.displayValue(this.item)
      }),
      onConfirm: this.onPublishConfirmed
    })
  }

  onPublishConfirmed = () => {
    this.saveItem();
  }

  confirmUnpublishing = () => {
    this.props.showConfirmModal({
      title: I18n.t("admin.projects.project.unpublishing.title"),
      message: I18n.t("admin.projects.project.unpublishing.message", {
        name: AdminProjectModel.displayValue(this.item)
      }),
      onConfirm: this.onUnpublishConfirmed
    })
  }

  onUnpublishConfirmed = () => {
    this.unpublish();
  }

  publish = () => {
    this.setState({
      submit: true
    })
    PublishProjectUseCase.execute(this.identifier)
    .then((result) => {
      const { data: { project } } = result;
      this.item = {
        ...this.item,
        ...project
      };

      this.setState({
        submit: false
      })
      this.forceUpdate();

      this.props.showSuccessMessage(I18n.t("admin.projects.project.publishing.success"));
      
    })
    .catch((result) => {
      const { errorData } = result;
      this.setState({
        submit: false,
        errorData: errorData
      });
      
      this.props.showErrorMessage(I18n.t("admin.projects.project.publishing.fail"));
    })
  }

  unpublish = () => {
    this.setState({
      submit: true
    })
    UnpublishProjectUseCase.execute(this.identifier)
    .then((result) => {
      const { data: { project } } = result;
      this.item = {
        ...this.item,
        ...project
      };

      this.setState({
        submit: false
      })
      this.forceUpdate();

      this.props.showSuccessMessage(I18n.t("admin.projects.project.unpublishing.success"));
      
    })
    .catch((result) => {
      const { errorData } = result;
      this.setState({
        submit: false,
        errorData: errorData
      });
      
      this.props.showErrorMessage(I18n.t("admin.projects.project.unpublishing.fail"));
    })
  }

  onChangeFilter = (name, value) => {
    const { changeLocationSearch } = this.props;
    
    changeLocationSearch({
      [name]: value
    }, {replaceLocation: true});
  }

  updateInvestmentParams = () => {
    const investmentsTab = this.investmentsTab();
    const { currentTab } = this.state;
    if(currentTab === InvestmentsTab) {
      investmentsTab.queryParams = this.props.filterData;
    }
  }

  investmentsTab = () => {
    return this.tabs.filter(tab => tab.identifier === InvestmentsTab)[0];
  }

  investmentsDefault = () => {
    return {
      statuses: ["applied", "paid"]
    }
  }

  header() {
    const { currentTab } = this.state;
    return <React.Fragment>
      <InfoPageHeader title = { this.pageTitle() }
                      subtitle = { this.pageSubtitle() } >
        { this.headerContent() }
      </InfoPageHeader>
      {
        this.identifier && this.item.isPublished &&
        <NavigationTabs tabs = { this.tabs }
                        currentTab = { currentTab }
                        onChangeTab = { this.onChangeTab } />
      }
    </React.Fragment>
  }

  headerContent() {
    const { classes } = this.props;
    const { submit, loading, anchor } = this.state;
    return <React.Fragment>
      {
        this.identifier && 
        <Button className = { clsx({
          [classes.publish]: true,
          [classes.published]: this.item.isPublished,
          [classes.notPublished]: !this.item.isPublished
        })} 
                disabled = { submit || loading } 
                onClick = { this.onPublishClicked } >
          { this.item.isPublished ? I18n.t("action.unpublish") : I18n.t("action.publish") }
        </Button>
      }
      {
        this.identifier && this.item.isPublished &&
        <div className = { classes.status } >
          <Button className = { classes.button }
                  disabled = { submit || loading } 
                  onClick = { this.onOpenStatusDropdown } 
                  ariaControls = { ProjectStatusesMenu } 
                  ariaHaspopup = { true } >
            { I18n.t("admin.projects.project.action.set_status") }
          </Button>
          <Menu id = { ProjectStatusesMenu }
                anchorEl = { anchor }
                keepMounted
                open = { Boolean(anchor) }
                onClose = { () => this.onCloseStatusDropdown() } >
            {
              ProjectStatusEnum.rawValues().filter(status => status !== this.item.status)
                              .map((status) => {
                return <MenuItem onClick = { () => this.onCloseStatusDropdown(status) } 
                                key = { status } >
                  { ProjectStatusEnum.label(status) }
                </MenuItem>
              })
            }
          </Menu>
        </div>
      }
    </React.Fragment>
  }

  processLoadedItem(result) {
    const { data: { project } } = result;
    this.item = project;
  }

  buttonsIsVisible() {
    const { currentTab } = this.state;
    return currentTab === InfoTab;
  }

  leftButtons() {
    const { classes } = this.props;
    const { submit, loading, filesIsLoaded } = this.state;

    return <React.Fragment>
      <Button className = { classes.button }
              type = { "submit" }
              disabled = { submit || loading || !filesIsLoaded } >
        { I18n.t("action.save") }
      </Button>
      {
        this.item && this.item.slug && 
        <ButtonLink className = { clsx(classes.button, classes.preview) }
                    variant = { ButtonVariantOutlined }
                    to = { WebRoutes.project(this.item) }
                    target = { "_blank" }>
          { I18n.t("shared.preview") }
        </ButtonLink>
      }
    </React.Fragment>
  }

  rightButtons() {
    const { classes } = this.props;
    const { submit, loading } = this.state;

    return <React.Fragment>
      {
        this.identifier && 
          <Button className = { clsx(classes.formButton, classes.delete) }
                disabled = { submit || loading }
                onClick = { this.onDelete } >
          { I18n.t("action.delete") }
        </Button>
      }
    </React.Fragment>
  }

  content() {
    const { errorData, currentTab, loading } = this.state;

    return <React.Fragment key = { this.item.key } >
      {
        !loading && <ProjectInfoTab project = { this.item } 
                                    validations = { this.validations }
                                    errorData = { errorData }
                                    onItemChanged = { this.onItemChange }
                                    visible = { currentTab === InfoTab } />
      }
      {
        !loading && this.identifier && <ProjectInvestmentsTab project = { this.item } 
                                                              visible = { currentTab === InvestmentsTab }
                                                              filterData = { {...this.investmentsTab().queryParams} }
                                                              onChangeFilter = { this.onChangeFilter } />
      }
      
      
    </React.Fragment>
  }
}

ProjectInfoPage.defaultProps = {
  dataModel: AdminProjectModel,
  newUseCase: NewProjectUseCase,
  showUseCase: ShowProjectUseCase,
  saveUseCase: SaveProjectUseCase,
  deleteUseCase: DeleteProjectUseCase,
  routeFunc: AdminRoutes.project
}

export default compose(
  withLocationSearchFilter,
  withShowToast,
  withShowModal,
  withCurrentUser,
  withScrollHandler,
  withStyles(
    (theme) => ({
      ...pageStyles(theme),
      ...infoPageStyles(theme),
      ...styles(theme),
    }),
    { withTheme: true }
  )
)(ProjectInfoPage);
